import {
    Directive, effect,
    ElementRef, forwardRef, HostListener,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from "@angular/forms";
import { LocalizedInputValue } from "./LocalizedInputValue";
import { FormLocalizationService } from "../form-localization.service";

export type localizedInputValidationError = {
    missingLanguages: string[]
}

@Directive({
    selector: 'input[type=text][localizedInput],' +
        'input[type=text][localizedInput][ngModel]' +
        'input[type=text][localizedInput][formControl]' +
        'input[type=text][localizedInput][formControlName]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => LocalizedInput)
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: LocalizedInput
        },
    ],
    standalone: true
})
export class LocalizedInput implements ControlValueAccessor, OnInit, OnDestroy, Validator
{
    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    private localizedInputValue: LocalizedInputValue = [];
    private activeLocalizeLanguage = "en-us";

    constructor(private elementRef: ElementRef, private _renderer: Renderer2, private _localizeService: FormLocalizationService)
    {
        effect(() => {
            let loc = _localizeService.localizationLanguage();
            this.activeLocalizeLanguage = loc.value;
            this.writeLocalizedValue(this.localizedInputValue);
        });
    }

    ngOnInit()
    {
    }

    ngOnDestroy()
    {
    }

    writeValue(value: any): void
    {
        this.writeLocalizedValue(value);
    }

    private writeLocalizedValue(value: any)
    {
        this.localizedInputValue = value as LocalizedInputValue;
        if (!this.localizedInputValue)
            return;

        let localizedValue = this.localizedInputValue.find((v) => v.language === this.activeLocalizeLanguage);
        if (localizedValue) {
            this._renderer.setProperty(this.elementRef.nativeElement, 'value', localizedValue.value);
        } else {
            this._renderer.setProperty(this.elementRef.nativeElement, 'value', '');
        }
    }

    registerOnChange(fn: any): void
    {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void
    {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void
    {
        this._renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    }

    @HostListener('input', ['$event'])
    onInput(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        const value = input.value;

        // new input arrive
        let currentValue = this.elementRef.nativeElement.value;

        // get the current LocalizedInputValue
        let localizedInputValue = this.localizedInputValue;

        // construct a new localize value
        let localizedValueIndex = localizedInputValue.findIndex((v) => v.language === this.activeLocalizeLanguage);
        if (localizedValueIndex !== -1) {
            // if a localized value for the active language already exists, update the value
            localizedInputValue[localizedValueIndex].value = currentValue;
        } else {
            // if a localized value for the active language doesn't exist, add a new localized value
            localizedInputValue.push({ value: currentValue, language: this.activeLocalizeLanguage });
        }

        // call the onChange method with the updated LocalizedInputValue
        this.onChange(localizedInputValue);
    }

    @HostListener('blur')
    onBlur(): void
    {
        this.onTouched();
    }

    markAsTouched()
    {
        this.onTouched();
    }

    validate(control: AbstractControl): ValidationErrors | null
    {
        const inputValue = control.value as LocalizedInputValue;

        let validationErrors = null;
        if (!inputValue || inputValue.length === 0) {
            validationErrors = {
                localizedInputRequired: <localizedInputValidationError>{
                    missingLanguages: []
                },
            };
        } else {
            // ensure that all localized values have a value
            for (const localizedValue of inputValue) {
                if (!localizedValue.value || localizedValue.value.trim().length === 0) {
                    if (validationErrors == null) {
                        validationErrors = {
                            localizedInputRequired: <localizedInputValidationError>{
                                missingLanguages: []
                            },
                        };
                    }

                    validationErrors.localizedInputRequired.missingLanguages.push(localizedValue.language);
                }
            }

        }

        return validationErrors;
    }
}
